import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { BrandMark } from "../components/Brand";
import StatusBadge from "../components/StatusBadge";
import { api, STATUS_LABELS, STATUS_ORDER, formatCurrency, formatRelative } from "../lib/api";
import { getAdmin, fetchAdminMe, adminLogout, saveAdmin } from "../lib/auth";
import { Search, LogOut, TrendingUp, Briefcase, CheckCircle2, Clock, Download, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { DeploymentPeriodCard } from "../components/metrics/DeploymentPeriodCard";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(getAdmin());
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchAdminMe();
        if (cancelled) return;
        setAdmin(me);
      } catch {
        // Auth missing/expired — clear stale localStorage and bounce to login
        try { localStorage.removeItem("instabridge_admin"); } catch (err) { console.error("clear-admin-state", err); }
        if (!cancelled) navigate("/login", { replace: true });
        return;
      }
      // Auth OK — load dashboard data. Failures here are toast-only, NOT redirects.
      try {
        const [a, s] = await Promise.all([api.get("/applications"), api.get("/analytics")]);
        if (!cancelled) { setApps(a.data); setStats(s.data); }
      } catch { if (!cancelled) toast.error("Failed to load dashboard"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([api.get("/applications"), api.get("/analytics")]);
      setApps(a.data); setStats(s.data);
    } catch { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    if (!search) return apps;
    const q = search.toLowerCase();
    return apps.filter((a) =>
      a.name.toLowerCase().includes(q) ||
      a.attorney_name.toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q)
    );
  }, [apps, search]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUS_ORDER.map((s) => [s, []]));
    filtered.forEach((a) => g[a.status]?.push(a));
    return g;
  }, [filtered]);

  const logout = async () => { await adminLogout(); navigate("/login"); };

  if (!admin) return (
    <div className="min-h-screen flex items-center justify-center text-[#6B7280]" data-testid="admin-loading-shell">Loading…</div>
  );

  const totalRequested = stats?.total_requested || 0;
  const fundedCount = stats?.by_status?.funded?.count || 0;
  const pendingCount = (stats?.by_status?.new?.count || 0) + (stats?.by_status?.waiting_attorney?.count || 0) + (stats?.by_status?.under_review?.count || 0);

  return (
    <div className="bg-[#F9FAFB] min-h-full" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="uppercase-label">Pipeline</div>
            <h1 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-semibold mt-1">All applications</h1>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or attorney…" className="pl-9" data-testid="dashboard-search" />
          </div>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/applications/export.csv`} download>
            <Button variant="outline" className="rounded-full" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </a>
          <Link to="/admin/invitations">
            <Button variant="outline" className="rounded-full" data-testid="invitations-link">
              <UserPlus className="w-4 h-4 mr-2" /> Invite attorney
            </Button>
          </Link>
        </div>

        {/* Pipeline (applications) */}
        <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-2" data-testid="pipeline-section-label">Application pipeline</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Stat icon={Briefcase} label="Total applications" value={stats?.total_applications || 0} sub="all statuses, incl. declined" testid="stat-total" />
          <Stat icon={Clock} label="In progress" value={pendingCount} testid="stat-pending" />
          <Stat icon={CheckCircle2} label="Funded applications" value={fundedCount} sub="applications marked funded" testid="stat-funded" />
          <Stat icon={TrendingUp} label="Requested · open pipeline" value={formatCurrency(stats?.open_pipeline_requested || 0)}
                sub={`${stats?.open_pipeline_count || 0} open applications · ${formatCurrency(totalRequested)} requested across all ${stats?.total_applications || 0}`} testid="stat-requested" />
        </div>

        {/* Actual funding (loans) — same canonical source as the Executive Dashboard */}
        <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-2" data-testid="funding-section-label">Actual funding</div>
        <div className="mb-8"><DeploymentPeriodCard compact /></div>

        {/* Kanban */}
        {loading ? (
          <div className="text-[#6B7280] text-sm" data-testid="dashboard-loading">Loading pipeline…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="kanban-board">
            {STATUS_ORDER.map((s) => (
              <div key={s} className="kanban-col p-3 flex flex-col" data-testid={`kanban-col-${s}`}>
                <div className="flex items-center justify-between px-1.5 pb-3 border-b border-[#E5E7EB] mb-3">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={s} />
                  </div>
                  <span className="text-xs text-[#6B7280] tabular-nums">{grouped[s].length}</span>
                </div>
                <div className="space-y-2.5 flex-1 min-h-[100px]">
                  {grouped[s].map((a) => (
                    <Link to={`/admin/applications/${a.id}`} key={a.id} className="block bg-white border border-[#E5E7EB] rounded-lg p-3.5 hover:-translate-y-0.5 hover:shadow-sm transition-all" data-testid={`kanban-card-${a.id}`}>
                      <div className="font-display font-semibold text-[#0A2540] text-sm">{a.name}</div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className={`inline-flex items-center px-1.5 h-4 text-[9px] uppercase tracking-wider rounded ${
                          (a.case_type || "auto_accident") === "workers_comp"
                            ? "bg-[#FEF3C7] text-[#92400E]"
                            : "bg-[#EFF6FF] text-[#1D4ED8]"
                        }`} data-testid={`kanban-casetype-${a.id}`}>
                          {(a.case_type || "auto_accident") === "workers_comp" ? "WC" : "MVA"}
                        </span>
                        <div className="text-xs text-[#6B7280] truncate flex-1 min-w-0">{a.accident_type}</div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <span className="font-display text-[#0066FF] font-semibold tabular-nums">{formatCurrency(a.requested_amount)}</span>
                        <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">{formatRelative(a.created_at)}</span>
                      </div>
                      <div className="text-[11px] text-[#6B7280] mt-2 truncate border-t border-[#F3F4F6] pt-2">
                        {a.attorney_name}
                      </div>
                    </Link>
                  ))}
                  {grouped[s].length === 0 && (
                    <div className="text-xs text-[#9CA3AF] text-center py-6 italic">No applications</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="uppercase-label">{label}</div>
      <Icon className="w-4 h-4 text-[#0066FF]" />
    </div>
    <div className="font-display text-3xl font-semibold text-[#0A2540] mt-3 tabular-nums">{value}</div>
    {sub && <div className="text-xs text-[#6B7280] mt-1">{sub}</div>}
  </div>
);
